import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert } from "@/components/Alert";
import { Loading } from "@/components/Loading";
import { Table } from "@/components/Table";
import { ProviderPagination } from "@/pages/Providers/ProviderPagination";
import { displayDate, displayValue } from "@/pages/Providers/display";
import {
    useProviderApi,
    useProviderContext,
} from "@/pages/Providers/useProviderApi";
import type {
    Catalogs,
    ProviderHistoryResponse,
} from "@/pages/Providers/types";

interface ProviderHistoryProps {
    providerId: number;
    version: string;
    catalogs: Catalogs;
}

export const ProviderHistory = ({
    providerId,
    version,
    catalogs,
}: ProviderHistoryProps) => {
    const { t, token, user, language } = useProviderContext();
    const { request } = useProviderApi();
    const [page, setPage] = useState(1);
    const query = useQuery({
        queryKey: [
            "provider-history",
            token,
            user.company,
            language,
            providerId,
            version,
            page,
        ],
        queryFn: ({ signal }) =>
            request<ProviderHistoryResponse>(
                `/${providerId}/history?page=${page}`,
                undefined,
                signal
            ),
        retry: false,
    });
    if (query.isPending) {
        return (
            <div className="relative min-h-32">
                <Loading text={t("providers.ui.loading")} />
            </div>
        );
    }
    if (query.isError) {
        return <Alert variant="error">{t("providers.errors.request")}</Alert>;
    }
    return (
        <div className="flex flex-col gap-4">
            <p className="text-xs text-slate-500">
                {t("providers.ui.history_permanent")}
            </p>
            {query.data.items.length === 0 && (
                <Alert variant="info">{t("providers.ui.history_empty")}</Alert>
            )}
            {query.data.items.map((entry) => (
                <article
                    key={entry.id}
                    className="rounded-xl border border-slate-200 p-4"
                >
                    <div className="flex flex-wrap justify-between gap-2 text-sm font-semibold">
                        <span>
                            {t(`providers.audit.${entry.action}`)} ·{" "}
                            {entry.actor_name}
                        </span>
                        <time dateTime={entry.created_at}>
                            {displayDate(entry.created_at, language)}
                        </time>
                    </div>
                    <p className="my-2 text-xs text-slate-500">
                        {t(`providers.sources.${entry.source}`)}
                    </p>
                    {Object.keys(entry.changes).length > 0 && (
                        <Table
                            paginated={false}
                            searchable={false}
                            data={Object.entries(entry.changes).map(
                                ([field, change]) => ({
                                    field: t(`providers.fields.${field}`),
                                    before: displayValue(
                                        field,
                                        change.before,
                                        catalogs,
                                        t
                                    ),
                                    after: displayValue(
                                        field,
                                        change.after,
                                        catalogs,
                                        t
                                    ),
                                })
                            )}
                            columns={[
                                {
                                    key: "field",
                                    label: t("providers.ui.field"),
                                },
                                {
                                    key: "before",
                                    label: t("providers.ui.before"),
                                },
                                {
                                    key: "after",
                                    label: t("providers.ui.after"),
                                },
                            ]}
                        />
                    )}
                    {entry.context?.resolved?.map((reason) => (
                        <p key={reason} className="mt-2 text-xs">
                            {t(`providers.reasons.${reason.split(":")[0]}`)}
                        </p>
                    ))}
                    {entry.context?.fingerprints?.length ? (
                        <p className="mt-2 text-xs">
                            {t("providers.ui.confirmed_warnings", {
                                count: entry.context.fingerprints.length,
                            })}
                        </p>
                    ) : null}
                    {entry.observation && (
                        <p className="mt-3 whitespace-pre-wrap text-sm">
                            {t("providers.ui.observation")}: {entry.observation}
                        </p>
                    )}
                </article>
            ))}
            <ProviderPagination
                page={page}
                total={query.data.total}
                pageSize={25}
                onPage={setPage}
            />
        </div>
    );
};
